import React, { useMemo } from 'react';
import { Box, InputAdornment, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import InlineTextField, { InlineTextFieldProps } from '@ui/InlineTextField';

type DesignAreaFieldProps = Pick<
  InlineTextFieldProps,
  | 'defaultValue'
  | 'editing'
  | 'fillValue'
  | 'formatInput'
  | 'onCancel'
  | 'onChange'
  | 'onClick'
  | 'onSave'
  | 'selectOnFocus'
  | 'reselectToken'
  | 'value'
  | 'variant'
> & {
  label: string;
  align?: 'left' | 'right';
  readonly?: boolean;
  unit?: React.ReactNode;
  valueEndAdornment?: React.ReactNode;
};

const useStyles = makeStyles<{ align: 'left' | 'right' }>()(
  (_theme, { align }) => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: align === 'left' ? 'flex-start' : 'flex-end',
      flexShrink: 0,
      width: 'max-content',
      minWidth: 'max-content',
    },
    value: {
      display: 'flex',
      alignItems: 'center',
      alignSelf: align === 'left' ? 'flex-start' : 'flex-end',
    },
    unit: {
      marginLeft: 0,
      color: 'rgba(0, 0, 0, 0.4)',
      fontSize: 18,
      fontWeight: 400,
      lineHeight: 'normal',
    },
    label: {
      paddingLeft: 4,
      paddingRight: 4,
      color: 'rgba(0, 0, 0, 0.6)',
      fontSize: 9,
      fontWeight: 400,
      letterSpacing: '0.7px',
      lineHeight: 1.56,
      textAlign: align,
      textTransform: 'uppercase',
    },
  }),
);

/** Shared two-line area value used by Design's editable GFA and read-only NIA. */
export const DesignAreaField: React.FC<DesignAreaFieldProps> = ({
  label,
  align = 'right',
  readonly = false,
  editing = false,
  unit = 'm²',
  valueEndAdornment,
  ...inputProps
}) => {
  const { classes } = useStyles({ align });
  const textFieldProps = useMemo(
    () => ({
      InputProps: {
        endAdornment: unit ? (
          <InputAdornment position="end" className={classes.unit}>
            <Typography component="span" className={classes.unit}>
              {unit}
            </Typography>
          </InputAdornment>
        ) : undefined,
        style: { paddingRight: 4 },
        inputProps: {
          'aria-label': label,
          tabIndex: readonly ? -1 : undefined,
          style: {
            color: 'rgba(0, 0, 0, 0.6)',
            padding: '0 4px',
            textAlign: align,
          },
        },
      },
      sx: {
        '& .MuiOutlinedInput-root': {
          paddingRight: '4px',
        },
        '& .MuiOutlinedInput-root.Mui-focused': {
          backgroundColor: 'common.white',
        },
        '& .MuiInputAdornment-positionEnd': {
          marginRight: 0,
          marginLeft: 0,
        },
        '& .MuiOutlinedInput-input': {
          overflow: 'visible',
          textOverflow: 'clip',
        },
      },
    }),
    [align, classes.unit, label, readonly, unit],
  );

  return (
    <Box className={classes.root}>
      <Box className={classes.value}>
        <InlineTextField
          {...inputProps}
          autoWidth
          autoWidthMin={0}
          autoWidthPadding={8}
          fullWidth={false}
          editing={editing}
          readonly={readonly}
          textFieldProps={textFieldProps}
        />
        {valueEndAdornment}
      </Box>
      <Typography className={classes.label}>{label}</Typography>
    </Box>
  );
};
